import type { ReactNode } from 'react';

// La barra de herramientas VERTICAL, pegada al borde derecho.
//
// Pedido de Daniel (20-oct-2026): «recuerda que esto se va a utilizar muchísimo
// en celular… de hecho la barra debería ir en formato vertical al lado derecho
// como se ve en las referencias de Procore y Fieldwire, porque actualmente la
// tienes horizontal y en la parte superior».
//
// Vive en su propio archivo porque va a dos lugares: nació en el editor de la
// foto (F-10, donde Daniel la aprobó el 21-oct) y de ahí pasa al visor del
// plano. «Una barra, dos lugares» fue su respuesta textual, y escribirla dentro
// del editor habría garantizado la segunda copia.
//
// Es PRESENTACIONAL a propósito: no sabe qué es una herramienta, solo pinta
// botones con uno activo. El visor del plano tiene submenús por grupo y el
// editor de la foto no; meter esa lógica acá sería diseñar para un caso que
// todavía no existe. El `children` es la ranura de abajo, donde cada lugar
// cuelga lo suyo (acá: la pastilla de color y grosor).
//
// Por qué derecha y no izquierda: el pulgar derecho tapa lo que toca. Con la
// barra a la derecha lo tapado es la barra misma; a la izquierda, con la mano
// cruzando, se tapa el dibujo justo mientras se elige con qué dibujar.

export interface BotonBarra<T extends string> {
  clave: T;
  titulo: string;
  icono: ReactNode;
}

export function BarraVertical<T extends string>({
  botones, activa, onElegir, children, className = '',
}: {
  botones: BotonBarra<T>[];
  activa: T;
  onElegir: (clave: T) => void;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      data-barra-vertical
      className={`absolute right-2 top-2 z-20 flex flex-col gap-1 rounded-xl border
                  border-white/10 bg-black/80 p-1.5 backdrop-blur ${className}`}
    >
      {botones.map(b => (
        <button
          key={b.clave}
          type="button"
          // La clave se expone en el DOM porque el `title` está en castellano y
          // puede cambiar de redacción: la prueba de UI necesita un agarre que
          // no se rompa al corregir una palabra.
          data-herramienta={b.clave}
          title={b.titulo}
          aria-label={b.titulo}
          onClick={() => onElegir(b.clave)}
          className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
            activa === b.clave
              ? 'bg-brand-green-100 text-black'
              : 'text-brand-gray-100 hover:bg-white/15'
          }`}
        >
          {b.icono}
        </button>
      ))}
      {children}
    </div>
  );
}
